import CardVisual from '@cohort/components-xps/components/cards/CardVisual';
import i18nComponentsXpsInstance from '@cohort/components-xps/lib/i18n';
import type {Language} from '@cohort/shared/schema/common';
import type {RaffleInstanceStatus} from '@cohort/shared/schema/common/perks';
import {getImageUrl, Sizes} from '@cohort/shared/utils/media';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {CaretRight} from '@phosphor-icons/react';
import React from 'react';
import {getI18n, useTranslation} from 'react-i18next';
type DigitalAssetOwnersOfPerkProps = {
  digitalAssetImageFileKey: string | null;
  hasDarkBg: boolean;
  perkAccessCounter: number;
};

const DigitalAssetOwnersOfPerk: React.FC<DigitalAssetOwnersOfPerkProps> = ({
  digitalAssetImageFileKey,
  perkAccessCounter,
  hasDarkBg,
}) => {
  const {t} = useTranslation('components', {
    keyPrefix: 'cards.perkCard',
    lng: getI18n().language,
    i18n: i18nComponentsXpsInstance,
  });

  if (perkAccessCounter === 0 || digitalAssetImageFileKey === null) {
    return <div />;
  }

  return (
    <div className="flex space-x-2">
      <img
        className="relative my-auto h-[16px] w-[16px] min-w-[16px] rounded"
        height={16}
        width={16}
        src={getImageUrl(import.meta.env.COHORT_ENV, digitalAssetImageFileKey, {
          h: Sizes.XS,
          w: Sizes.XS,
        })}
        alt="Digital asset visual"
      />

      <div className={classNames('text-sm', hasDarkBg ? 'text-white/60' : 'text-black/60')}>
        {t('includedIn', {count: perkAccessCounter})}
      </div>
    </div>
  );
};

export type PerkCard = {
  perkVideoFileKey: string | null | File;
  perkThumbnailFileKey: string | null | File;
  perkBannerFileKey: string | File | null;
  perkDisplayName: string;
  canBeUsed: boolean | undefined;
  digitalAssetImageFileKey: string | null;
  perkAccessCounter?: number;
  handleAction?: () => void;
  hasDarkBg: boolean;
  accentColor: string;
  trackPlayVideoClick?: () => void;
  selectedLanguage?: Language;
  hideMediaCover?: boolean;
  raffleStatus: RaffleInstanceStatus | undefined | null;
  statusMessage: string | undefined;
};

type PerkCardProps = {
  perkCard: PerkCard;
};

const PerkCard: React.FC<PerkCardProps> = ({perkCard}): React.ReactElement => {
  const {t} = useTranslation('components', {
    keyPrefix: 'cards.perkCard',
    lng: perkCard.selectedLanguage ?? getI18n().language,
    i18n: i18nComponentsXpsInstance,
  });

  const {
    perkVideoFileKey,
    digitalAssetImageFileKey,
    perkBannerFileKey,
    perkThumbnailFileKey,
    perkAccessCounter,
    perkDisplayName,
    canBeUsed,
    handleAction,
    hasDarkBg,
    accentColor,
    trackPlayVideoClick,
    hideMediaCover,
    raffleStatus,
    statusMessage,
  } = perkCard;

  const hasVideoMedia = perkVideoFileKey !== null;

  const getStatusMessage = (): string => {
    if (raffleStatus !== null && raffleStatus !== undefined) {
      if (raffleStatus === 'won' && canBeUsed === false) {
        return t('used');
      }
      if (statusMessage === undefined) {
        throw new Error('Raflle is defined and StatusMessage is undefined');
      }
      return statusMessage;
    }
    if (canBeUsed === true) {
      return t('available');
    }
    return t('used');
  };

  return (
    <div
      className={classNames(
        'grid w-full min-w-[var(--perk-card-min-width)] gap-4 overflow-hidden rounded-xl p-4 shadow-sm',
        handleAction !== undefined && 'cursor-pointer',
        hasDarkBg ? 'bg-white/8' : 'bg-black/4',
        hideMediaCover ? 'grid-cols-1' : '[grid-template-rows:var(--perk-card-visual-height)_1fr]'
      )}
      onClick={e => {
        if (!hasVideoMedia && handleAction !== undefined) {
          e.stopPropagation();
          handleAction();
        }
      }}
    >
      {!hideMediaCover && (
        <CardVisual
          bannerFileKey={perkBannerFileKey}
          thumbnailFileKey={perkThumbnailFileKey}
          videoFileKey={perkVideoFileKey}
          visualName={perkDisplayName}
          trackPlayVideoClick={trackPlayVideoClick}
          enforce16x9AspectRatio
        />
      )}
      <div
        className="space-y-2 rounded-b-xl"
        onClick={
          handleAction
            ? e => {
                e.stopPropagation();
                handleAction();
              }
            : undefined
        }
      >
        {canBeUsed !== undefined && (
          <div className="flex space-x-2">
            <div
              className={classNames(
                'my-auto h-2 w-2 rounded-full',
                hasDarkBg ? 'bg-white' : 'bg-black'
              )}
            />
            <div className={classNames('text-sm', hasDarkBg ? 'text-white/60' : 'text-black/60')}>
              {getStatusMessage()}
            </div>
          </div>
        )}
        <div className="flex justify-between">
          <div
            className={classNames('text-lg font-semibold', hasDarkBg ? 'text-white' : 'text-black')}
          >
            {perkDisplayName}
          </div>
          {handleAction !== undefined && <CaretRight size={24} style={{color: accentColor}} />}
        </div>
        {perkAccessCounter !== undefined && (
          <DigitalAssetOwnersOfPerk
            digitalAssetImageFileKey={digitalAssetImageFileKey}
            perkAccessCounter={perkAccessCounter}
            hasDarkBg={hasDarkBg}
          />
        )}
      </div>
    </div>
  );
};

export default PerkCard;
